import React, { useLayoutEffect } from "react";
import {
  LayoutDashboard,
  CalendarCheck,
  Calendar,
  CalendarClock,
  Star,
  Settings,
  MessageSquare,
  HelpCircle,
  LogOut,
  ChevronLeft,
} from "lucide-react";
import IMAGES from "../../constants/images";
import { ToastContainer, toast } from "react-toastify";
import { navigateTo, navigateToAdmin } from "../../utils/navigation";

export default function Sidebar({ collapsed, setCollapsed }) {
  // Publish the current sidebar width so the TopBar can start right after it;
  // reset on unmount so pages without a sidebar keep a full-width TopBar
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--sidebar-w", collapsed ? "64px" : "272px");
    return () => root.style.setProperty("--sidebar-w", "0px");
  }, [collapsed]);

  const routes = [
    {
      slug: "dashboard",
      title: "Dashboard",
      path: "/dashboard",
      icon: LayoutDashboard,
    },
    {
      slug: "appointment",
      title: "Appointments",
      path: "/upcoming-appointments/upcoming",
      icon: CalendarCheck,
    },
    {
      slug: "events",
      title: "Events",
      path: "/events",
      icon: Calendar,
    },
    // {
    //   slug: "reports",
    //   title: "Reports",
    //   path: "/",
    //   icon: FileText,
    // },
    {
      slug: "availability",
      title: "Availability",
      path: "/set-availability/available",
      icon: CalendarClock,
    },
    {
      slug: "ratings",
      title: "Ratings & Feedback",
      path: "/",
      icon: Star,
    },
  ];

  const tools = [
    {
      slug: "settings",
      title: "Settings",
      path: "/profile",
      icon: Settings,
    },
    {
      slug: "support",
      title: "Support",
      path: "/support",
      icon: MessageSquare,
    },
    {
      slug: "help",
      title: "Help Center",
      path: "/help_center",
      icon: HelpCircle,
    },
  ];

  const handleNavigation = (path) => {
    if (path === "/") {
      toast.warning("Sorry, this route is unavailable at the moment.");
    } else if (path === "/logout") {
      localStorage.clear();
      navigateToAdmin();
    } else {
      navigateTo(path);
    }
  };

  // Highlight the item whose first path segment matches the current URL
  const isActive = (path) => {
    if (typeof window === "undefined" || path === "/") return false;
    const segment = path.split("/")[1];
    return window.location.pathname.split("/").includes(segment);
  };

  // Note: Bootstrap's `.border` utility is !important, so borders here use
  // `border-[1px] border-solid` to keep Tailwind's colors.
  const renderItem = (item) => {
    const active = isActive(item.path);
    const Icon = item.icon;
    return (
      <button
        key={item.slug}
        type="button"
        onClick={() => handleNavigation(item.path)}
        title={collapsed ? item.title : ""}
        className={`group w-full flex items-center gap-3 py-2.5 mb-1 rounded-xl transition-all duration-200 ${
          collapsed ? "justify-center px-0" : "px-3"
        } ${
          active
            ? "bg-white/10 text-white shadow-lg backdrop-blur-md border-[1px] border-solid border-white/10"
            : "bg-transparent text-white/70 hover:bg-white/5 hover:text-white border-0"
        }`}
      >
        <Icon
          size={20}
          className={`flex-shrink-0 transition-colors ${
            active ? "text-emerald-300" : "text-white/60 group-hover:text-white"
          }`}
        />
        {!collapsed && (
          <span className="font-poppins font-medium tracking-wide text-sm truncate text-left">
            {item.title}
          </span>
        )}
      </button>
    );
  };

  return (
    <div
      className={`hidden lg:flex bg-gradient-to-b from-blue-950 to-emerald-950 text-white shadow-2xl transition-all duration-300 ease-in-out rounded-r-3xl flex-col overflow-hidden h-[calc(100vh/var(--app-zoom))] ${collapsed ? "w-[64px]" : "w-[272px]"
        } `}
    >
      {/* Logo + collapse toggle */}
      {!collapsed ? (
        <div className="flex items-center justify-between px-3 h-16 flex-shrink-0">
          <div
            className="flex items-center gap-2 cursor-pointer group min-w-0"
            onClick={() => handleNavigation("/dashboard")}
          >
            <div className="w-9 h-9 rounded-full bg-emerald-900/50 flex items-center justify-center overflow-hidden shrink-0 group-hover:scale-105 transition-transform">
              <img
                src={IMAGES.phyber_logo_white}
                alt="Phyber"
                className="w-5 h-5 object-contain select-none pointer-events-none"
                draggable={false}
              />
            </div>
            <img
              src={IMAGES.footer_logo}
              alt="Phyber"
              className="h-4 w-auto object-contain opacity-95 group-hover:opacity-100 transition-opacity select-none pointer-events-none"
              draggable={false}
            />
          </div>
          <button
            type="button"
            onClick={() => setCollapsed(!collapsed)}
            className="w-8 h-8 p-0 rounded-xl bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-all border-[1px] border-solid border-white/10 flex items-center justify-center shadow-sm flex-shrink-0"
            title="Collapse sidebar"
          >
            <ChevronLeft size={18} className="text-white/80 flex-shrink-0" />
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-center h-16 flex-shrink-0">
          <button
            type="button"
            onClick={() => setCollapsed(!collapsed)}
            className="w-9 h-9 p-0 rounded-full bg-emerald-900/50 flex items-center justify-center overflow-hidden border-0 hover:scale-105 transition-transform"
            title="Expand sidebar"
          >
            <img
              src={IMAGES.phyber_logo_white}
              alt="Phyber"
              className="w-5 h-5 object-contain select-none pointer-events-none"
              draggable={false}
            />
          </button>
        </div>
      )}

      {/* Main Navigation */}
      <div className="flex-1 overflow-y-auto pt-2 pb-4 px-2">
        {routes.map(renderItem)}
      </div>

      {/* Tools Section */}
      <div className="px-2 pt-3 pb-2 bg-black/10 border-t-[1px] border-solid border-x-0 border-b-0 border-white/10 flex-shrink-0">
        <p
          className={`text-[10px] font-poppins font-semibold uppercase tracking-wider text-white/40 mb-2 ${
            collapsed ? "text-center" : "px-3"
          }`}
        >
          Tools
        </p>
        {tools.map(renderItem)}
      </div>

      {/* Footer: Powered by + Logout */}
      <div className="bg-black/20 border-t-[1px] border-solid border-x-0 border-b-0 border-white/5 flex-shrink-0">
        {!collapsed ? (
          <div className="flex items-center gap-1 px-4 pt-3 pb-1">
            <span className="text-[11px] font-poppins text-white/50 whitespace-nowrap">
              Powered by
            </span>
            <img
              src={IMAGES.footer_logo}
              alt="footer_logo"
              className="h-3 object-contain opacity-80"
            />
          </div>
        ) : null}
        <div className="px-2 pb-2 pt-1">
          <button
            type="button"
            onClick={() => handleNavigation("/logout")}
            title={collapsed ? "Logout" : ""}
            className={`group w-full flex items-center gap-3 py-2.5 rounded-xl border-0 bg-transparent text-white/70 hover:bg-white/5 hover:text-white transition-all ${
              collapsed ? "justify-center px-0" : "px-3"
            }`}
          >
            <LogOut
              size={20}
              className="flex-shrink-0 text-white/60 group-hover:text-red-300 transition-colors"
            />
            {!collapsed && (
              <span className="font-poppins font-medium tracking-wide text-sm">
                Logout
              </span>
            )}
          </button>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        closeOnClick={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </div>
  );
}
